import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Inbox } from 'lucide-react';
import { api } from '../lib/api.js';
import { FloatingPanel } from './FloatingPanel.jsx';

const POLL_MS = 60_000;

/**
 * Sino de notificações (NOTIF-01): badge com contagem de não lidas (polling de
 * 60s), dropdown com as últimas notificações, marcar lida ao clicar e
 * "marcar todas". O clique navega para o link da notificação quando houver.
 */
export function NotificationBell({ className }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [itens, setItens] = useState(null); // null = ainda não carregou
  const anchorRef = useRef(null);
  const navigate = useNavigate();

  const refreshCount = useCallback(async () => {
    try {
      const res = await api.countNotificacoesNaoLidas();
      setCount(res?.data?.nao_lidas ?? 0);
    } catch {
      /* silencioso — polling tenta de novo */
    }
  }, []);

  const loadList = useCallback(async () => {
    try {
      const res = await api.listNotificacoes(20);
      setItens(res?.data?.itens || []);
      setCount(res?.data?.nao_lidas ?? 0);
    } catch {
      setItens([]);
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const t = window.setInterval(refreshCount, POLL_MS);
    return () => window.clearInterval(t);
  }, [refreshCount]);

  useEffect(() => {
    if (open) loadList();
  }, [open, loadList]);

  const handleItemClick = async (n) => {
    setOpen(false);
    if (!n.lida) {
      api.marcarNotificacaoLida(n.id).catch(() => {});
      setCount((c) => Math.max(0, c - 1));
    }
    if (n.link) navigate(n.link);
  };

  const handleMarcarTodas = async () => {
    try {
      await api.marcarTodasNotificacoesLidas();
      setCount(0);
      setItens((arr) => (arr || []).map((n) => ({ ...n, lida: true })));
    } catch {
      /* mantém estado atual */
    }
  };

  return (
    <>
      <button
        ref={anchorRef}
        onClick={() => setOpen((o) => !o)}
        aria-label={count > 0 ? `Notificações (${count} não lidas)` : 'Notificações'}
        title="Notificações"
        className={className}
      >
        <Bell size={18} strokeWidth={count > 0 ? 2.5 : 2} />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[17px] h-[17px] px-1 rounded-full bg-danger text-white text-[9px] font-700 grid place-items-center leading-none ring-2 ring-transparent animate-pulse">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      <FloatingPanel
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        align="end"
        minWidth={320}
        estimatedHeight={400}
        className="flex flex-col"
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
          <p className="text-sm font-700 text-text">Notificações</p>
          {count > 0 && (
            <button
              onClick={handleMarcarTodas}
              className="flex items-center gap-1 text-xs font-600 text-accent hover:underline"
            >
              <CheckCheck size={13} /> Marcar todas como lidas
            </button>
          )}
        </div>

        <div className="max-h-[340px] overflow-y-auto">
          {itens === null && (
            <p className="px-4 py-6 text-sm text-text-muted text-center">Carregando…</p>
          )}
          {itens?.length === 0 && (
            <div className="px-4 py-8 text-center text-text-muted">
              <Inbox size={22} className="mx-auto mb-2 opacity-60" />
              <p className="text-sm">Nenhuma notificação por aqui.</p>
            </div>
          )}
          {itens?.map((n) => (
            <button
              key={n.id}
              onClick={() => handleItemClick(n)}
              className={[
                'w-full text-left px-4 py-2.5 border-b border-border/60 last:border-b-0 transition-colors hover:bg-surface-hover',
                n.lida ? 'opacity-70' : 'bg-accent/5',
              ].join(' ')}
            >
              <div className="flex items-start gap-2">
                {!n.lida && <span className="mt-1.5 w-2 h-2 rounded-full bg-accent shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm font-600 text-text truncate">{n.titulo}</p>
                  {n.mensagem && (
                    <p className="text-xs text-text-secondary line-clamp-2">{n.mensagem}</p>
                  )}
                  <p className="text-[10px] text-text-muted mt-0.5">
                    {new Date(n.criado_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </FloatingPanel>
    </>
  );
}
